/**
 * Journey Analytics Component
 * Advanced analytics for student journey tracking
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  Clock, 
  Award,
  ArrowRight,
  BarChart3,
  PieChart,
  Loader2,
  ChevronDown,
  ChevronUp,
  Target,
  Zap,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { journeyAPI } from '@/lib/api';
import { toast } from 'sonner';

// Stage labels for display
const STAGE_LABELS = {
  inquiry: 'Inquiry',
  lead_validated: 'Lead Validated',
  application_received: 'App Received',
  application_submitted: 'App Submitted',
  offer_issued: 'Offer Issued',
  offer_accepted: 'Offer Accepted',
  uco: 'UCO',
  deposit: 'Deposit',
  cas_stage: 'CAS Stage',
  refund: 'Refund',
  visa_issued: 'Visa Issued',
  enrolment_completed: 'Enrolled',
  commission_received: 'Commission'
};

// Stage colors
const STAGE_COLORS = {
  inquiry: 'bg-gray-400',
  lead_validated: 'bg-blue-400',
  application_received: 'bg-cyan-400',
  application_submitted: 'bg-indigo-400',
  offer_issued: 'bg-purple-400',
  offer_accepted: 'bg-pink-400',
  uco: 'bg-amber-400',
  deposit: 'bg-orange-400',
  cas_stage: 'bg-teal-400',
  refund: 'bg-rose-400',
  visa_issued: 'bg-sky-400',
  enrolment_completed: 'bg-emerald-400',
  commission_received: 'bg-teal-600'
};

export const JourneyAnalytics = ({ onStageClick }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [showAgentDetails, setShowAgentDetails] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await journeyAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load journey analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-gray-500">No analytics data available</p>
      </div>
    );
  }

  const maxStageCount = Math.max(...Object.values(analytics.stage_distribution || {}), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm text-gray-500">Total Applications</span>
          </div>
          <p className="text-3xl font-semibold text-gray-800">{analytics.total_applications}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-sm text-gray-500">Enrolled Rate</span>
          </div>
          <p className="text-3xl font-semibold text-gray-800">{analytics.funnel_summary?.inquiry_to_enrolled || 0}%</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm text-gray-500">Offer Acceptance</span>
          </div>
          <p className="text-3xl font-semibold text-gray-800">{analytics.funnel_summary?.offer_to_acceptance || 0}%</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-teal-600" />
            </div>
            <span className="text-sm text-gray-500">Active Agents</span>
          </div>
          <p className="text-3xl font-semibold text-gray-800">{analytics.agent_performance?.length || 0}</p>
        </div>
      </div>

      {/* Funnel Visualization - Clickable */}
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-teal-600" />
            Student Journey Funnel
          </h3>
          <p className="text-xs text-gray-500">Click any stage to view applications</p>
        </div>
        
        <div className="space-y-2">
          {Object.entries(analytics.stage_distribution || {}).map(([stage, count], index) => {
            const percentage = ((count / maxStageCount) * 100).toFixed(0);
            const color = STAGE_COLORS[stage] || 'bg-gray-400';
            const hasApplications = count > 0;
            
            return (
              <div 
                key={stage} 
                className={`flex items-center gap-4 p-2 rounded-lg transition-all ${
                  hasApplications 
                    ? 'cursor-pointer hover:bg-[#e9ecef]/50 active:scale-[0.99]' 
                    : 'opacity-60'
                }`}
                onClick={() => hasApplications && onStageClick && onStageClick(stage)}
              >
                <div className="w-28 sm:w-36 flex-shrink-0">
                  <p className={`text-sm font-medium truncate ${hasApplications ? 'text-gray-800' : 'text-gray-400'}`}>
                    {STAGE_LABELS[stage] || stage}
                  </p>
                </div>
                <div className="flex-1 h-10 bg-[#e9ecef] rounded-lg overflow-hidden relative group">
                  <div 
                    className={`h-full ${color} transition-all duration-500 flex items-center justify-end pr-3 ${
                      hasApplications ? 'group-hover:brightness-110' : ''
                    }`}
                    style={{ width: `${Math.max(percentage, count > 0 ? 8 : 2)}%` }}
                  >
                    {count > 0 && (
                      <span className="text-sm font-semibold text-slate-900">{count}</span>
                    )}
                  </div>
                  {hasApplications && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink className="w-4 h-4 text-gray-800" />
                    </div>
                  )}
                </div>
                <div className="w-14 text-right">
                  <span className={`text-sm ${hasApplications ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion Rates */}
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-teal-600" />
          Stage Conversion Rates
        </h3>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(analytics.conversion_rates || []).slice(0, 9).map((conv, index) => (
            <div key={index} className="bg-[#e9ecef]/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-gray-200 text-gray-600 text-xs">
                  {STAGE_LABELS[conv.from_stage] || conv.from_stage}
                </Badge>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <Badge className="bg-gray-200 text-gray-600 text-xs">
                  {STAGE_LABELS[conv.to_stage] || conv.to_stage}
                </Badge>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-2xl font-semibold text-gray-800">{conv.conversion_rate}%</p>
                  <p className="text-xs text-gray-400">{conv.from_count} → {conv.to_count}</p>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  conv.conversion_rate >= 80 ? 'bg-emerald-100' :
                  conv.conversion_rate >= 50 ? 'bg-teal-100' :
                  'bg-red-100'
                }`}>
                  <TrendingUp className={`w-5 h-5 ${
                    conv.conversion_rate >= 80 ? 'text-emerald-600' :
                    conv.conversion_rate >= 50 ? 'text-teal-600' :
                    'text-red-600'
                  }`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Average Time at Each Stage */}
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <Clock className="w-5 h-5 text-teal-600" />
          Average Time at Each Stage (Days)
        </h3>
        
        <div className="flex flex-wrap gap-3">
          {Object.entries(analytics.avg_stage_duration_days || {}).map(([stage, days]) => (
            <div key={stage} className="bg-[#e9ecef]/50 rounded-lg px-4 py-3 flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${STAGE_COLORS[stage] || 'bg-gray-400'}`} />
              <div>
                <p className="text-xs text-gray-500">{STAGE_LABELS[stage] || stage}</p>
                <p className="text-lg font-semibold text-gray-800">{days} <span className="text-xs text-gray-500">days</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Agent Performance */}
      {analytics.agent_performance && analytics.agent_performance.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              Agent Performance by Journey Completion
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAgentDetails(!showAgentDetails)}
              className="text-gray-500"
            >
              {showAgentDetails ? 'Hide Details' : 'Show Details'}
              {showAgentDetails ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
          </div>
          
          <div className="space-y-4">
            {analytics.agent_performance
              .sort((a, b) => b.completion_rate - a.completion_rate)
              .slice(0, showAgentDetails ? 20 : 5)
              .map((agent, index) => (
                <div key={index} className="bg-[#e9ecef]/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 flex items-center justify-center">
                        <span className="text-teal-600 font-semibold">{agent.agent_name?.charAt(0) || '?'}</span>
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium">{agent.agent_name}</p>
                        <p className="text-xs text-gray-500">{agent.total_applications} applications</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-semibold text-gray-800">{agent.completion_rate}%</p>
                      <p className="text-xs text-gray-500">completion rate</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-400 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${agent.completion_rate}%` }}
                    />
                  </div>

                  {/* Stage breakdown */}
                  {showAgentDetails && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                      {Object.entries(agent.stage_breakdown || {})
                        .filter(([_, count]) => count > 0)
                        .map(([stage, count]) => (
                          <Badge key={stage} className="bg-gray-200/50 text-gray-600 text-xs">
                            {STAGE_LABELS[stage] || stage}: {count}
                          </Badge>
                        ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyAnalytics;
